import React, { useState, useEffect, useRef } from "react";
import { X, ChevronDown, Check, SlidersHorizontal, Loader2, Table2, Pin } from "lucide-react";

const STORAGE_KEY = "studentgroup_pinned_filters";
const STORAGE_PINNED_KEY = "studentgroup_filters_pinned";

export function getStoredFilters() {
  try {
    const pinned = localStorage.getItem(STORAGE_PINNED_KEY) === "true";
    if (!pinned) return null;
    const raw = localStorage.getItem(STORAGE_KEY);
    return raw ? JSON.parse(raw) : null;
  } catch {
    return null;
  }
}
import api, { baseURL } from "~/services/api";
import { saveAs } from "file-saver";
import { toast } from "react-toastify";

const DateInputWithClear = ({ label, value, onChange, inputBaseClasses }) => (
  <div className="flex flex-col gap-1">
    <label className="text-[10px] font-bold text-gray-400 uppercase">{label}</label>
    <div className="relative w-full">
      <input
        type="date"
        className={`${inputBaseClasses} pr-10`}
        value={value}
        onChange={onChange}
      />
      {value && (
        <button
          type="button"
          onClick={() => onChange({ target: { value: "" } })}
          className="absolute top-1/2 right-2 transform -translate-y-1/2 text-gray-400 hover:text-red-500 p-1 rounded-full transition-colors bg-white"
          title="Clear date"
        >
          <X size={12} />
        </button>
      )}
    </div>
  </div>
);

const SearchableSelect = ({ label, options, value, onChange, placeholder = "All" }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [query, setQuery] = useState("");
  const containerRef = useRef(null);

  useEffect(() => {
    const found = options.find((o) => o.value === value);
    setQuery(found ? found.label : "");
  }, [value, options]);

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (containerRef.current && !containerRef.current.contains(e.target)) {
        setIsOpen(false);
        const found = options.find((o) => o.value === value);
        setQuery(found ? found.label : "");
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [value, options]);

  const filtered = options.filter((o) =>
    o.label.toLowerCase().includes((query || "").toLowerCase())
  );

  return (
    <div className="flex flex-col gap-1.5" ref={containerRef}>
      <label className="text-xs font-bold text-gray-600 uppercase tracking-wide">{label}</label>
      <div className="relative">
        <input
          type="text"
          className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm text-gray-800 focus:outline-none focus:ring-2 focus:ring-orange-500 pr-8"
          placeholder={placeholder}
          value={query}
          onChange={(e) => { setQuery(e.target.value); setIsOpen(true); }}
          onFocus={() => setIsOpen(true)}
        />
        <button
          type="button"
          onClick={() => setIsOpen((o) => !o)}
          className="absolute right-2 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600"
        >
          <ChevronDown size={16} className={`transition-transform ${isOpen ? "rotate-180" : ""}`} />
        </button>
        {isOpen && (
          <div className="absolute z-50 mt-1 w-full bg-white shadow-xl max-h-48 rounded-md py-1 text-sm overflow-auto border border-gray-200 top-full left-0">
            <button
              type="button"
              onClick={() => { onChange(""); setQuery(""); setIsOpen(false); }}
              className="w-full text-left px-4 py-2 hover:bg-orange-50 text-gray-400 italic"
            >
              All
            </button>
            {filtered.length === 0 ? (
              <div className="px-4 py-2 text-gray-500 italic">No options found</div>
            ) : (
              filtered.map((option) => (
                <button
                  key={option.value}
                  type="button"
                  onClick={() => { onChange(option.value); setQuery(option.label); setIsOpen(false); }}
                  className={`w-full text-left px-4 py-2 hover:bg-orange-50 flex items-center justify-between ${
                    option.value === value ? "bg-orange-50 text-orange-700 font-medium" : "text-gray-700"
                  }`}
                >
                  {option.label}
                  {option.value === value && <Check size={14} />}
                </button>
              ))
            )}
          </div>
        )}
      </div>
    </div>
  );
};

const STATUS_OPTIONS = [
  { value: "In Formation", label: "In Formation" },
  { value: "Ongoing", label: "Ongoing" },
  { value: "Paused", label: "Paused" },
  { value: "Finished", label: "Finished" },
];

const EMPTY_FILTERS = {
  level: "",
  status: "",
  workload: "",
  teacher: "",
  startDateFrom: "",
  startDateTo: "",
  endDateFrom: "",
  endDateTo: "",
};

export default function ModalFilters({ opened = false, onClose = () => null, onApplyFilters = () => null, capacityView = false }) {
  const [formData, setFormData] = useState(() => {
    try {
      const raw = localStorage.getItem(STORAGE_KEY);
      return raw ? JSON.parse(raw) : EMPTY_FILTERS;
    } catch {
      return EMPTY_FILTERS;
    }
  });
  const [pinned, setPinned] = useState(() => localStorage.getItem(STORAGE_PINNED_KEY) === "true");
  const [isClosing, setIsClosing] = useState(false);
  const [loadingOptions, setLoadingOptions] = useState(false);
  const [exportingExcel, setExportingExcel] = useState(false);
  const [options, setOptions] = useState({ levels: [], workloads: [], teachers: [] });

  const inputBaseClasses =
    "w-full border border-gray-300 rounded-md px-3 py-2 text-sm text-gray-800 focus:outline-none focus:ring-2 focus:ring-orange-500";

  useEffect(() => {
    if (!opened) return;
    setLoadingOptions(true);
    api.get("/studentgroupfilters")
      .then(({ data }) => {
        setOptions({
          levels: (data.levels || []).map((l) => ({ value: l.id, label: l.name })),
          workloads: (data.workloads || []).map((w) => ({ value: w.id, label: w.name })),
          teachers: (data.teachers || []).map((s) => ({
            value: s.id,
            label: `${s.name}${s.last_name ? " " + s.last_name : ""}`,
          })),
        });
      })
      .catch((err) => console.error(err))
      .finally(() => setLoadingOptions(false));
  }, [opened]);

  const handleChange = (field, value) => setFormData((prev) => ({ ...prev, [field]: value }));

  const handleTogglePin = () => {
    const next = !pinned;
    setPinned(next);
    localStorage.setItem(STORAGE_PINNED_KEY, String(next));
    if (!next) {
      localStorage.removeItem(STORAGE_KEY);
    } else {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(formData));
    }
  };

  const handleClose = () => {
    setIsClosing(true);
    setTimeout(() => { setIsClosing(false); onClose(); }, 300);
  };

  const handleExportExcel = async () => {
    setExportingExcel(true);
    try {
      const params = {};
      if (formData.level) params.level_id = formData.level;
      if (formData.status) params.status = formData.status;
      if (formData.workload) params.workload_id = formData.workload;
      if (formData.teacher) params.staff_id = formData.teacher;
      if (formData.startDateFrom) params.start_date_from = formData.startDateFrom;
      if (formData.startDateTo) params.start_date_to = formData.startDateTo;
      if (formData.endDateFrom) params.end_date_from = formData.endDateFrom;
      if (formData.endDateTo) params.end_date_to = formData.endDateTo;
      params.orderBy = "name";
      params.orderASC = "ASC";

      const { data } = await api.get("/studentgroups/excel", { params });
      const name = data?.name || "studentgroups_export.xlsx";
      saveAs(`${baseURL}/get-file/${name}`, name);
      toast("Excel generated successfully!", { type: "success", autoClose: 2500 });
      handleClose();
    } catch (err) {
      console.error("Error exporting excel:", err);
      toast(err?.response?.data?.error || "Failed to generate Excel", { type: "error", autoClose: 3000 });
    } finally {
      setExportingExcel(false);
    }
  };

  const handleApply = () => {
    const params = new URLSearchParams();
    params.set("limit", "50");
    params.set("page", "1");
    params.set("orderBy", "name");
    params.set("orderASC", "ASC");
    params.set("search", "");

    params.set("capacity_view", "true");
    if (formData.level) params.set("level_id", formData.level);
    if (formData.status) params.set("status", formData.status);
    if (formData.workload) params.set("workload_id", formData.workload);
    if (formData.teacher) params.set("staff_id", formData.teacher);
    if (formData.startDateFrom) params.set("start_date_from", formData.startDateFrom);
    if (formData.startDateTo) params.set("start_date_to", formData.startDateTo);
    if (formData.endDateFrom) params.set("end_date_from", formData.endDateFrom);
    if (formData.endDateTo) params.set("end_date_to", formData.endDateTo);

    if (pinned) {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(formData));
    }
    onApplyFilters(`/studentgroups?${params.toString()}`);
    handleClose();
  };

  if (!opened) return null;

  return (
    <>
      <style>{`
        @keyframes slideInRight { from { transform: translateX(100%); opacity: 0; } to { transform: translateX(0); opacity: 1; } }
        @keyframes slideOutRight { from { transform: translateX(0); opacity: 1; } to { transform: translateX(100%); opacity: 0; } }
        .modal-slide-in { animation: slideInRight 0.3s ease-out forwards; }
        .modal-slide-out { animation: slideOutRight 0.3s ease-in forwards; }
      `}</style>

      <div
        className="fixed inset-0 z-50 flex justify-end items-center py-6"
        style={{ backgroundColor: "rgba(0,0,0,.75)" }}
        onClick={(e) => { if (e.target === e.currentTarget) handleClose(); }}
      >
        <div className={`bg-white w-full max-w-md h-full flex flex-col shadow-2xl overflow-hidden rounded-l-2xl ${isClosing ? "modal-slide-out" : "modal-slide-in"}`}>

          {/* Header */}
          <div className="flex items-center justify-between px-6 py-6 border-b border-gray-200 bg-white shrink-0">
            <div className="flex flex-col gap-1">
              <h2 className="text-lg font-bold text-gray-900 uppercase tracking-tight flex items-center gap-2">
                <SlidersHorizontal size={18} /> Filters
              </h2>
              <p className="text-xs text-gray-500 font-medium">Refine your student groups</p>
            </div>
            <div className="flex items-center gap-2">
              <button
                type="button"
                onClick={handleTogglePin}
                title={pinned ? "Unpin filters" : "Pin filters"}
                className={`p-2 rounded-md transition-colors ${pinned ? "bg-orange-100 text-orange-600 hover:bg-orange-200" : "hover:bg-gray-100 text-gray-400 hover:text-gray-600"}`}
              >
                <Pin size={18} className={pinned ? "fill-orange-500" : ""} />
              </button>
              <button
                type="button"
                onClick={handleClose}
                className="p-2 rounded-md hover:bg-gray-100 text-gray-500 hover:text-gray-800 transition-colors"
              >
                <X size={20} />
              </button>
            </div>
          </div>

          {/* Body */}
          <div className="flex-1 overflow-y-auto px-6 py-6 text-sm text-gray-700">
            {loadingOptions ? (
              <div className="flex items-center justify-center h-full gap-2 text-gray-500">
                <Loader2 className="animate-spin" size={18} /> Loading filters...
              </div>
            ) : (
              <div className="flex flex-col gap-6">
                <div className="flex flex-col gap-5">
                  {!capacityView && (
                    <SearchableSelect
                      label="Level"
                      options={options.levels}
                      value={formData.level}
                      onChange={(val) => handleChange("level", val)}
                    />
                  )}
                  <SearchableSelect
                    label="Status"
                    options={STATUS_OPTIONS}
                    value={formData.status}
                    onChange={(val) => handleChange("status", val)}
                  />
                  {!capacityView && (
                    <>
                      <SearchableSelect
                        label="Workload"
                        options={options.workloads}
                        value={formData.workload}
                        onChange={(val) => handleChange("workload", val)}
                      />
                      <SearchableSelect
                        label="Teacher"
                        options={options.teachers}
                        value={formData.teacher}
                        onChange={(val) => handleChange("teacher", val)}
                      />
                    </>
                  )}
                </div>

                {!capacityView && (
                  <>
                    <div className="pt-6 border-t border-gray-200">
                      <p className="text-xs font-bold text-gray-400 uppercase mb-4 tracking-wider">Start Date Range</p>
                      <div className="grid grid-cols-2 gap-3">
                        <DateInputWithClear
                          label="From"
                          value={formData.startDateFrom}
                          onChange={(e) => handleChange("startDateFrom", e.target.value)}
                          inputBaseClasses={inputBaseClasses}
                        />
                        <DateInputWithClear
                          label="To"
                          value={formData.startDateTo}
                          onChange={(e) => handleChange("startDateTo", e.target.value)}
                          inputBaseClasses={inputBaseClasses}
                        />
                      </div>
                    </div>

                    <div className="pt-2">
                      <p className="text-xs font-bold text-gray-400 uppercase mb-4 tracking-wider">End Date Range</p>
                      <div className="grid grid-cols-2 gap-3">
                        <DateInputWithClear
                          label="From"
                          value={formData.endDateFrom}
                          onChange={(e) => handleChange("endDateFrom", e.target.value)}
                          inputBaseClasses={inputBaseClasses}
                        />
                        <DateInputWithClear
                          label="To"
                          value={formData.endDateTo}
                          onChange={(e) => handleChange("endDateTo", e.target.value)}
                          inputBaseClasses={inputBaseClasses}
                        />
                      </div>
                    </div>
                  </>
                )}
              </div>
            )}
          </div>

          {/* Footer */}
          <div className="px-6 py-6 border-t border-gray-200 bg-gray-50 shrink-0 flex flex-col gap-3">
            <button
              type="button"
              onClick={handleExportExcel}
              disabled={exportingExcel}
              className="w-full py-2 text-sm font-medium text-white bg-green-600 border border-green-600 rounded-lg hover:bg-green-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
            >
              {exportingExcel ? (
                <>
                  <Loader2 size={16} className="animate-spin" />
                  Exporting...
                </>
              ) : (
                <>
                  <Table2 size={16} />
                  Export Excel
                </>
              )}
            </button>
            <button
              type="button"
              onClick={() => {
                setFormData(EMPTY_FILTERS);
                localStorage.removeItem(STORAGE_KEY);
              }}
              className="w-full py-2 text-sm font-medium text-red-600 bg-white border border-red-100 rounded-lg hover:bg-red-50 transition-colors"
            >
              Clean All Filters
            </button>
            <div className="flex gap-3 w-full">
              <button
                type="button"
                onClick={handleClose}
                className="flex-1 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={handleApply}
                className="flex-1 py-2 text-sm font-medium text-white bg-sky-700 border border-sky-700 rounded-lg hover:bg-sky-800 transition-colors"
              >
                Apply Filters
              </button>
            </div>
          </div>

        </div>
      </div>
    </>
  );
}
